import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, authHelpers, ensureProfile } from '../lib/supabase';
import { ordersService } from '../services/ordersService';

interface Profile {
  id: string;
  username: string;
  email: string | null;
  bio: string | null;
  avatar?: string;
  avatar_frame?: string;
  role: 'user' | 'admin';
  is_premium: boolean;
  premium_until: string | null;
  is_banned: boolean;
  verified: boolean;
  created_at: string;
  daily_listing_count?: number;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  sessionLoading: boolean;
  profileLoading: boolean;
  isAdminResolved: boolean;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateProfileState: (updates: Partial<Profile>) => void;
  isPremium: boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [isAdminResolved, setIsAdminResolved] = useState(false);

  // ✅ Check expired premiums
  useEffect(() => {
    ordersService.checkExpiredPremiums();

    const interval = setInterval(() => {
      ordersService.checkExpiredPremiums();
    }, 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const loadProfile = async (userId: string) => {
    setProfileLoading(true);
    setIsAdminResolved(false);
    
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        setProfileLoading(false);
        setIsAdminResolved(true);
        return;
      }

      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        throw error;
      }

      if (!profileData) {
        const newProfile = await ensureProfile(currentUser);
        setProfile(newProfile);
      } else {
        // ✅ Auto-update expired premium
        if (profileData.is_premium && profileData.premium_until) {
          const premiumUntil = new Date(profileData.premium_until);
          const now = new Date();
          
          if (premiumUntil < now) {
            await supabase
              .from('profiles')
              .update({ is_premium: false })
              .eq('id', userId);
            
            profileData.is_premium = false;
          }
        }
        
        setProfile(profileData);
      }
      
      setIsAdminResolved(true);
    } catch (error: any) {
      // ✅ PERFORMANCE: Simplified retry logic - only one retry attempt
      try {
        await new Promise(resolve => setTimeout(resolve, 500));
        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();
        
        if (existingProfile) {
          setProfile(existingProfile);
          setIsAdminResolved(true);
          setProfileLoading(false);
          return;
        }
      } catch (retryError) {
        // Silent fail
      }
      
      // ✅ PERFORMANCE: Safe fallback profile
      const fallbackProfile = {
        id: userId,
        username: 'user_' + userId.slice(0, 8),
        email: null,
        bio: null,
        avatar_frame: 'none',
        role: 'user' as const,
        is_premium: false,
        premium_until: null,
        is_banned: false,
        verified: false,
        created_at: new Date().toISOString(),
      };
      setProfile(fallbackProfile);
      setIsAdminResolved(true);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setIsAdminResolved(true);
      }
      setSessionLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setIsAdminResolved(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, username: string) => {
    await authHelpers.signUp(email, password, username);
  };

  const signIn = async (email: string, password: string) => {
    await authHelpers.signIn(email, password);
  };

  const signOut = async () => {
    await authHelpers.signOut();
    setUser(null);
    setProfile(null);
    setIsAdminResolved(true);
  };

  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id);
    }
  };

  const updateProfileState = (updates: Partial<Profile>) => {
    if (profile) {
      const updatedProfile = { ...profile, ...updates };
      setProfile(updatedProfile);
    }
  };

  const isPremium = profile?.is_premium && profile?.premium_until 
    ? new Date(profile.premium_until) > new Date() 
    : false;

  const isAdmin = profile?.role === 'admin';

  const effectiveIsPremium = isAdmin || isPremium;

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading: sessionLoading || profileLoading,
        sessionLoading,
        profileLoading,
        isAdminResolved,
        signUp,
        signIn,
        signOut,
        refreshProfile,
        updateProfileState,
        isPremium: effectiveIsPremium,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
